import {PreparedAppAsset} from './assetManifest';

export type SignedAssetUploadInstructions = {
  asset_sha256: string;
  asset_ttl_class: string;
  checksum_sha256: string;
  content_length: number;
  content_type: string;
  put_url: string;
};

export type SignedAssetUploadEntry = {
  asset: PreparedAppAsset;
  instructions: SignedAssetUploadInstructions;
};

export type SignedAssetUploadErrorCode =
  | 'invalid_instructions'
  | 'asset_mismatch'
  | 'upload_failed';

export class SignedAssetUploadError extends Error {
  constructor(
    readonly code: SignedAssetUploadErrorCode,
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'SignedAssetUploadError';
  }
}

type Fetch = typeof globalThis.fetch;

const validateInstructions = (
  asset: PreparedAppAsset,
  instructions: SignedAssetUploadInstructions
) => {
  let putUrl: URL;
  try {
    putUrl = new URL(instructions.put_url);
  } catch {
    throw new SignedAssetUploadError('invalid_instructions', 'The signed upload URL is invalid');
  }
  if (putUrl.protocol !== 'https:' || putUrl.username || putUrl.password) {
    throw new SignedAssetUploadError('invalid_instructions', 'The signed upload URL is not secure');
  }
  if (
    !instructions.checksum_sha256 ||
    !instructions.asset_sha256 ||
    !instructions.asset_ttl_class
  ) {
    throw new SignedAssetUploadError(
      'invalid_instructions',
      'The signed upload headers are incomplete'
    );
  }
  if (
    asset.size !== instructions.content_length ||
    asset.contentType !== instructions.content_type ||
    asset.sha256 !== instructions.asset_sha256
  ) {
    throw new SignedAssetUploadError(
      'asset_mismatch',
      `Signed upload instructions do not match ${asset.logicalPath}`
    );
  }
};

export const putSignedAsset = async (
  {asset, instructions}: SignedAssetUploadEntry,
  options: {fetch?: Fetch; signal?: AbortSignal} = {}
) => {
  validateInstructions(asset, instructions);
  const fetcher = options.fetch ?? globalThis.fetch;
  const response = await fetcher(instructions.put_url, {
    method: 'PUT',
    body: asset.file,
    credentials: 'omit',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
    signal: options.signal,
    headers: {
      'Content-Type': instructions.content_type,
      'x-amz-checksum-sha256': instructions.checksum_sha256,
      'x-amz-meta-asset-sha256': instructions.asset_sha256,
      'x-amz-meta-asset-ttl-class': instructions.asset_ttl_class,
    },
  });

  if (!response.ok) {
    throw new SignedAssetUploadError(
      'upload_failed',
      `Asset upload failed with status ${response.status}`,
      response.status
    );
  }
};

export const putSignedAssets = async (
  entries: SignedAssetUploadEntry[],
  options: {
    concurrency?: number;
    fetch?: Fetch;
    onProgress?: (completed: number, total: number, logicalPath: string) => void;
    signal?: AbortSignal;
  } = {}
) => {
  const concurrency = options.concurrency ?? 4;
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new RangeError('Upload concurrency must be a positive integer');
  }

  let nextIndex = 0;
  let completed = 0;
  let hasFailed = false;
  let firstError: unknown;
  const worker = async () => {
    while (nextIndex < entries.length && !hasFailed) {
      const entry = entries[nextIndex++];
      try {
        await putSignedAsset(entry, options);
        completed += 1;
        options.onProgress?.(completed, entries.length, entry.asset.logicalPath);
      } catch (error) {
        if (!hasFailed) {
          hasFailed = true;
          firstError = error;
        }
      }
    }
  };

  await Promise.all(Array.from({length: Math.min(concurrency, entries.length)}, () => worker()));
  if (hasFailed) {
    throw firstError;
  }
};
